/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Centralised image presets for `<DynamicImage>`.
 *
 * Each preset bundles `widths`, `heights` (optional), `quality`, and a
 * `priority` recommendation for one shopper-facing surface (PDP hero, product
 * tile, recommendation carousel, etc.). Call sites pass `preset={IMAGE_PRESETS.X}`
 * to the component instead of repeating raw `widths`/`priority` values per surface.
 *
 * The widths and quality values mirror the legacy Scene7 srcsets defined under
 * `cartridges/site_ashleyhomestore_us` so the React storefront keeps perceptual
 * parity with the existing SFRA site for the Scene7 → Storefront-Next migration
 * (BROWSE-48). Quality defaults: HERO=80, TILE=70, RECO=65, ZOOM=85.
 */

import type { DynamicImageDimensions } from '@/lib/images/dynamic-image';

export type ImagePreset = {
    widths?: DynamicImageDimensions;
    heights?: DynamicImageDimensions;
    quality?: number;
    priority?: 'high' | 'low' | 'auto';
};

export const IMAGE_PRESETS = {
    /** PDP hero — first viewport image, LCP candidate. */
    HERO: {
        widths: ['100vw', '680px'],
        quality: 80,
        priority: 'high',
    },
    /** PDP gallery thumbnail strip. */
    GALLERY_THUMB: {
        widths: [80, 96],
        quality: 70,
    },
    /** PDP zoom — high-quality enlarged image. */
    ZOOM: {
        widths: [1200, 1600],
        quality: 85,
    },
    /** PDP secondary view (dimensions / lifestyle). */
    DIMENSION: {
        widths: [600, 800, 1000],
        quality: 75,
    },
    /**
     * PLP / search results product tile — refinements panel closed (full-width grid).
     * base/sm: 2 cols (~48vw), md: 3 cols (~32vw), lg+: 4 cols (~24vw).
     * Aligned with PRODUCT_IMAGE_BREAKPOINTS (320/640/750/828/1080/1200px).
     */
    TILE: {
        widths: ['48vw', '48vw', '32vw', '24vw', '24vw', '24vw'],
        quality: 70,
    },
    /**
     * PLP / search results product tile — refinements panel open (sidebar reduces available width).
     * base/sm/md: 2 cols (~48vw), lg+: 3 cols inside sidebar-reduced container (~22–25vw).
     * Aligned with PRODUCT_IMAGE_BREAKPOINTS (320/640/750/828/1080/1200px).
     */
    TILE_WITH_REFINEMENTS: {
        widths: ['48vw', '48vw', '48vw', '22vw', '25vw', '25vw'],
        quality: 70,
    },
    /** Mini-cart line item thumbnail. */
    MINI_CART: {
        widths: [80, 96],
        heights: [80, 96],
        quality: 70,
    },
    /** Cart line item / order summary thumbnail. */
    CART_LINE: {
        widths: [120, 160],
        heights: [120, 160],
        quality: 70,
    },
    /**
     * Recommendation carousel tile. Lower quality is intentional — these tiles
     * are below the fold and quality differences are imperceptible at the rendered size.
     *
     * Carousel CSS: `w-[21.75rem] md:w-64 2xl:w-72` (Tailwind md=768px, 2xl=1536px).
     * Widths cover all 9 PRODUCT_IMAGE_BREAKPOINTS (320/640/750/828/1080/1200/1920/2048/3840px):
     *   - < 768px  (320/640/750):    item is 348px (21.75rem)
     *   - ≥ 768px  (828/1080/1200):  item is 256px (w-64 / 16rem)
     *   - ≥ 1536px (1920/2048/3840): item is 288px (2xl:w-72 / 18rem)
     * Consecutive equal values are deduplicated by DynamicImage into a single <source>.
     */
    RECOMMENDATION: {
        widths: ['348px', '348px', '348px', '256px', '256px', '256px', '288px', '288px', '288px'],
        quality: 65,
    },
    /** Colour / material swatch (PDP variant rail). */
    SWATCH: {
        widths: [40, 56],
        quality: 70,
    },
    /** Homepage / category hero banner. */
    BANNER_HERO: {
        widths: ['100vw'],
        quality: 80,
        priority: 'high',
    },
    /** Medium tri-banner content tile. */
    BANNER_TRI: {
        widths: [300, 480, 600, 800],
        quality: 70,
    },
} as const satisfies Record<string, ImagePreset>;

export type ImagePresetKey = keyof typeof IMAGE_PRESETS;
